import { IconButton } from '@creditornot/cb-button';
import { Cross } from '@creditornot/cb-icons';
import { cbSemanticCssVars, colors, typographyCss } from '@creditornot/cb-ingredients';
import { Tag } from '@creditornot/cb-tag';
import { Decimal } from 'decimal.js';
import { DateTime } from 'luxon';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { WoltModal } from 'common/components';
import { Typography as T } from 'common/typography';

import LocalizedMonetaryAmount from './LocalizedMonetaryAmount';
import {
  DELIVERY_STATUSES,
  DELIVERY_TYPES,
  SETTLEMENT_REASON_TAG_VARIANT,
  SETTLEMENT_REASONS,
  SETTLEMENT_STATUS_TAG_VARIANT,
  SETTLEMENT_STATUSES,
  SETTLEMENT_TYPES,
} from './constants';
import { Settlement } from '../../apis/restaurant-api/merchant-admin/settlements/types';

type Props = {
  onClose: () => void;
  settlement: Settlement | null;
  timezone: string;
};

const Root = styled(WoltModal).attrs({ position: 'right' })`
  background-color: ${cbSemanticCssVars.surfaceMain};
  width: 400px;
`;

const SettlementDetails = styled.div`
  padding: 24px;
  /* NOTE: Extra padding to fit the Intercom chat bubble */
  padding-block-end: 70px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-inline: 24px;
  padding-block: 20px;
  /* ignore-color */
  box-shadow: inset 0 -1px 0 ${colors.pepper12};
`;

const Section = styled.div`
  margin-block-end: 32px;
`;

const SectionTitle = styled(T.Title1)`
  display: block;
  margin-block-end: 24px;
`;

const Row = styled.div`
  margin-block-end: 16px;
`;

const OrderDetails = styled.div`
  margin-block-start: 32px;
  margin-block-end: 54px;
`;

const AmountRow = styled(Row)<{ positive: boolean }>`
  color: ${({ positive }) => (positive ? colors.lime100 : colors.strawberry100)};
`;

const CloseButton = styled(IconButton).attrs({ size: 'small', variant: 'black' })`
  margin-inline-start: -3px;
`;

const FieldLabel = styled.div`
  ${typographyCss.Small()}
  color: ${cbSemanticCssVars.textSecondary};
  margin-block-end: 4px;
`;

const FieldValue = styled.div`
  ${typographyCss.Body3()}
`;

const MultiplicationSign = styled.div`
  ::before {
    content: '×';
    margin: 0 4px;
  }
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
`;

const ItemInfo = styled.div`
  display: flex;
  justify-content: space-between;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  padding: 12px 0;
`;

const ItemCount = styled.div.attrs({ fontWeight: 'bold' })`
  ${typographyCss.Body3()}
  display: flex;
  min-width: 36px;
`;

const ItemOptions = styled.div``;

const ItemOption = styled.div`
  display: flex;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  padding: 12px 0;
`;

const ItemOptionCount = styled.div`
  ${typographyCss.Body3()}
  display: flex;
  margin-inline-start: 36px;
  min-width: 36px;
  color: ${cbSemanticCssVars.textPrimary};
`;

const ItemOptionValue = styled.div`
  ${typographyCss.Body3()}
  flex: 1;
  ${({ theme }) =>
    theme.cbDirection === 'ltr'
      ? css`
          padding-inline-end: 16px;
        `
      : css`
          padding-inline-start: 16px;
        `}
  color: ${cbSemanticCssVars.textPrimary};
`;

const ItemOptionPrice = styled.div`
  ${typographyCss.Body3()}
  color: ${cbSemanticCssVars.textPrimary};
`;

const ItemName = styled.div`
  ${typographyCss.Body3()}
  flex: 1;
  ${({ theme }) =>
    theme.cbDirection === 'ltr'
      ? css`
          padding-inline-end: 16px;
        `
      : css`
          padding-inline-start: 16px;
        `}
`;

const ItemPrice = styled.div`
  ${typographyCss.Body3()}
`;

const SettlementDetailsSidePanel = ({ onClose, settlement, timezone }: Props) => {
  const status = settlement?.settled ? 'settled' : 'not_settled';
  const isPositive = !!settlement && new Decimal(settlement.amount.amount).greaterThanOrEqualTo(0);
  return (
    <Root isOpen={settlement !== null} onRequestClose={onClose}>
      {settlement !== null && (
        <>
          <Header>
            <T.Heading6>
              <FormattedMessage id="settlements.settlement-details.details" />
            </T.Heading6>
            <CloseButton onClick={onClose}>
              <Cross />
            </CloseButton>
          </Header>
          <SettlementDetails>
            {settlement.purchase && (
              <Section>
                <SectionTitle>
                  <FormattedMessage id="settlements.settlement-details.order-information" />
                </SectionTitle>

                <Row>
                  <FieldLabel>
                    <FormattedMessage id="settlements.settlement-details.order-placed-at" />
                  </FieldLabel>
                  <FieldValue>
                    {DateTime.fromISO(settlement.purchase.order_placed_at)
                      .setZone(timezone)
                      .toFormat('dd.MM.yyyy HH:mm')}
                  </FieldValue>
                </Row>

                <Row>
                  <FieldLabel>
                    <FormattedMessage id="settlements.columns.order-number" />
                  </FieldLabel>
                  <FieldValue>#{settlement.purchase.order_number}</FieldValue>
                </Row>
                <Row>
                  <FieldLabel>
                    <FormattedMessage id="settlements.columns.customer" />
                  </FieldLabel>
                  <FieldValue>
                    {settlement.customer_name
                      ? `${settlement.customer_name.first_name} ${settlement.customer_name.last_name}`
                      : '-'}
                  </FieldValue>
                </Row>
                <Row>
                  <FieldLabel>
                    <FormattedMessage id="purchases.delivery-method" />
                  </FieldLabel>
                  <FieldValue>
                    <FormattedMessage id={DELIVERY_TYPES[settlement.purchase.delivery_method]} />
                  </FieldValue>
                </Row>
                <Row>
                  <FieldLabel>
                    <FormattedMessage id="purchases.status" />
                  </FieldLabel>
                  <FieldValue>
                    <FormattedMessage id={DELIVERY_STATUSES[settlement.purchase.order_status]} />
                  </FieldValue>
                </Row>
                <OrderDetails>
                  <FieldLabel>
                    <FormattedMessage id="purchases.headings.order-details" />
                  </FieldLabel>
                  {settlement.purchase.order_details.map((item, index) => (
                    <Item key={`${item.id}-${index}`}>
                      <ItemInfo>
                        <ItemCount>
                          {item.count} <MultiplicationSign />
                        </ItemCount>
                        <ItemName>{item.name}</ItemName>
                        <ItemPrice>
                          <LocalizedMonetaryAmount
                            amount={parseFloat(item.total_price.amount)}
                            currency={item.total_price.currency}
                          />
                        </ItemPrice>
                      </ItemInfo>

                      <ItemOptions>
                        {item.options.map((option, idx) => (
                          <ItemOption key={`${option.id}-${idx}`}>
                            <ItemOptionCount>
                              {option.count} <MultiplicationSign />
                            </ItemOptionCount>
                            <ItemOptionValue>{option.value}</ItemOptionValue>
                            {!new Decimal(option.price.amount).equals(new Decimal(0)) && (
                              <ItemOptionPrice>
                                (
                                <LocalizedMonetaryAmount
                                  amount={parseFloat(option.price.amount)}
                                  currency={option.price.currency}
                                />
                                )
                              </ItemOptionPrice>
                            )}
                          </ItemOption>
                        ))}
                      </ItemOptions>
                    </Item>
                  ))}
                </OrderDetails>
              </Section>
            )}
            <Section>
              <SectionTitle>
                <FormattedMessage id="settlements.settlement-details.additions-and-deductions" />
              </SectionTitle>
              <AmountRow positive={isPositive}>
                <FieldLabel>
                  <FormattedMessage id="settlements.columns.amount" />
                </FieldLabel>
                <FieldValue>
                  <LocalizedMonetaryAmount
                    amount={parseFloat(settlement.amount.amount)}
                    currency={settlement.amount.currency}
                  />
                </FieldValue>
              </AmountRow>
              <Row>
                <FieldLabel>
                  <FormattedMessage id="settlements.columns.settlement-type" />
                </FieldLabel>
                <FieldValue>
                  <FormattedMessage id={SETTLEMENT_TYPES[settlement.type]} />
                </FieldValue>
              </Row>
              <Row>
                <FieldLabel>
                  <FormattedMessage id="settlements.columns.reason" />
                </FieldLabel>
                {settlement.reason && SETTLEMENT_REASONS[settlement.reason] ? (
                  <Tag variant={SETTLEMENT_REASON_TAG_VARIANT} uppercase>
                    <FormattedMessage id={SETTLEMENT_REASONS[settlement.reason]} />
                  </Tag>
                ) : (
                  <>-</>
                )}
              </Row>
              <Row>
                <FieldLabel>
                  <FormattedMessage id="settlements.columns.status" />
                </FieldLabel>
                {SETTLEMENT_STATUSES[status] ? (
                  <Tag variant={SETTLEMENT_STATUS_TAG_VARIANT[status]} uppercase>
                    <FormattedMessage id={SETTLEMENT_STATUSES[status]} />
                  </Tag>
                ) : (
                  <>-</>
                )}
              </Row>
              <Row>
                <FieldLabel>
                  <FormattedMessage id="settlements.columns.created-at" />
                </FieldLabel>
                <FieldValue>
                  {DateTime.fromISO(settlement.created_at).setZone(timezone).toFormat('dd.MM.yyyy HH:mm')}
                </FieldValue>
              </Row>
              <Row>
                <FieldLabel>
                  <FormattedMessage id="settlements.columns.comment" />
                </FieldLabel>
                <FieldValue>{settlement.note}</FieldValue>
              </Row>
            </Section>
          </SettlementDetails>
        </>
      )}
    </Root>
  );
};

export default SettlementDetailsSidePanel;
